"use client"

import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

const agentKeys = ["madeira", "orchestrator", "architect", "executor", "verifier"] as const
const agentColors = ["teal", "amber", "slate", "coral", "purple"] as const

const colorMap = {
  teal: {
    border: "border-teal-500/30",
    bg: "bg-teal-500/5",
    text: "text-teal-500",
    dot: "bg-teal-500"
  },
  amber: {
    border: "border-amber-500/30",
    bg: "bg-amber-500/5",
    text: "text-amber-500",
    dot: "bg-amber-500"
  },
  slate: {
    border: "border-slate-400/30",
    bg: "bg-slate-400/5",
    text: "text-slate-400",
    dot: "bg-slate-400"
  },
  coral: {
    border: "border-rose-500/30",
    bg: "bg-rose-500/5",
    text: "text-rose-500",
    dot: "bg-rose-500"
  },
  purple: {
    border: "border-purple-500/30",
    bg: "bg-purple-500/5",
    text: "text-purple-500",
    dot: "bg-purple-500"
  }
}

export function AgentModelSection() {
  const t = useTranslations("techLab.agents")
  
  return (
    <section className="py-24">
      {/* Section header */}
      <div className="text-center mb-16">
        <p className="text-slate-400 text-sm font-medium uppercase tracking-widest mb-3">
          {t("sectionLabel")}
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
          {t("title")}
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          {t("subtitle")}
        </p>
      </div>

      {/* Agent cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agentKeys.map((key, idx) => {
          const colors = colorMap[agentColors[idx]]
          
          return (
            <div
              key={idx}
              className={cn(
                "p-6 rounded-2xl border transition-all duration-300 hover:shadow-lg",
                colors.border,
                colors.bg
              )}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={cn("w-3 h-3 rounded-full", colors.dot)} />
                <div>
                  <h3 className="font-bold text-foreground">{t(`${key}.name`)}</h3>
                  <p className={cn("text-xs font-medium", colors.text)}>{t(`${key}.role`)}</p>
                </div>
              </div>

              {/* Function */}
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                {t(`${key}.function`)}
              </p>

              {/* Access level */}
              <div className="pt-4 border-t border-border/30">
                <p className="text-xs text-muted-foreground/70">
                  <span className="font-medium text-foreground/80">{t("access")}:</span> {t(`${key}.access`)}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Human-on-the-loop note */}
      <div className="mt-12 p-6 rounded-xl bg-accent-secondary/5 border border-accent-secondary/20 text-center">
        <p className="text-foreground/90">
          <span className="font-semibold">{t("humanLoop.label")}:</span>{" "}
          <span className="text-muted-foreground">
            {t("humanLoop.description")}
          </span>
        </p>
      </div>
    </section>
  )
}
